import React, { createContext, useContext, useState, useMemo, useCallback, useRef } from 'react';

const FilterContext = createContext(null);

export const useFilters = () => {
  const ctx = useContext(FilterContext);
  if (!ctx) throw new Error('useFilters must be used within FilterProvider');
  return ctx;
};

const defaultDocTypes = [
  { id: 'PhD Informative', name: 'PhD Informative', color: '#FFD54F' },
  { id: 'PhD Dissertation', name: 'PhD Dissertation', color: '#FFC107' },
  { id: 'Research Paper', name: 'Research Paper', color: '#6d28d9' },
  { id: 'Technical Drawing', name: 'Technical Drawing / Schematic', color: '#a855f7' },
  { id: 'Blueprint', name: 'Blueprint / Diagram', color: '#9333ea' },
  { id: 'Patent', name: 'Patent Document', color: '#c026d3' },
  { id: 'Instructional Manual', name: 'Instructional Manual', color: '#d946ef' },
  { id: 'Personal Report (Organic)', name: 'Personal Report (Organic)', color: '#10b981' },
  { id: 'Personal Report (Collected)', name: 'Personal Report (Collected)', color: '#14b8a6' },
  { id: 'News Article', name: 'News Article', color: '#3b82f6' },
  { id: 'Academic Paper', name: 'Academic Paper', color: '#FFC107' },
  { id: 'Government', name: 'Government', color: '#ef4444' },
  { id: 'Wiki', name: 'Wiki', color: '#f59e0b' },
  { id: 'Blog Post', name: 'Blog Post', color: '#92400E' },
  { id: 'Forum', name: 'Forum', color: '#06b6d4' },
  { id: 'Video', name: 'Video', color: '#92400E' },
  { id: 'PDF Document', name: 'PDF Document', color: '#dc2626' },
  { id: 'MS Word Document', name: 'MS Word Document', color: '#2563eb' },
  { id: 'Webpage', name: 'Webpage', color: '#6b7280' }
];

export const FilterProvider = ({ children }) => {
  // Educated Individual Sites (the PRIMARY filter hub)
  const [educatedIndividualFilters, setEducatedIndividualFilters] = useState({
    science: true, engineering: true, construction: true, culinary: true,
    trades: true, mathematics: true, technology: true,
    favor_visuals: false, favor_pictures: false
  });

  // Per-topic min word count for EI Sites
  const [eiMinWords, setEiMinWords] = useState({
    science: 4000, engineering: 4000, construction: 3000,
    culinary: 2000, trades: 3000, mathematics: 3000, technology: 4000
  });

  // Global min word count
  const [minWordCount, setMinWordCount] = useState(4000);

  // Document types
  const documentTypes = useMemo(() => defaultDocTypes, []);
  const [selectedDocTypes, setSelectedDocTypes] = useState(() => defaultDocTypes.map(d => d.id));

  // Per-type min word counts for doc types
  const [docTypeMinWords, setDocTypeMinWords] = useState({
    'PhD Informative': 8000, 'PhD Dissertation': 10000, 'Research Paper': 5000,
    'Technical Drawing': 500, 'Blueprint': 500, 'Patent': 3000,
    'Instructional Manual': 2000, 'Personal Report (Organic)': 1000,
    'Personal Report (Collected)': 1000, 'News Article': 800,
    'Academic Paper': 5000, 'Government': 2000, 'Wiki': 1500,
    'Blog Post': 800, 'Forum': 300, 'Video': 0,
    'PDF Document': 2000, 'MS Word Document': 2000, 'Webpage': 500
  });

  // Certification filters - UNCHECKED BY DEFAULT
  const [certificationFilters, setCertificationFilters] = useState({
    pearsonvue: false, government: false, advancedDegree: false
  });

  // Per-cert min word counts
  const [certMinWords, setCertMinWords] = useState({
    pearsonvue: 3000, government: 2000, advancedDegree: 5000
  });

  // Content preference min words
  const [contentMinWords, setContentMinWords] = useState({
    articles: 1000, tutorials: 2000, research: 5000, news: 800,
    forums: 300, blogs: 800, videos: 0, documents: 2000
  });

  // Quality, match, depth, aggregation
  const [minQualityScore, setMinQualityScore] = useState(0);
  const [exactMatch, setExactMatch] = useState(false);
  const [searchDepth, setSearchDepth] = useState(5);
  const [aggregation, setAggregation] = useState('and_or');

  // Selected categories (shared across pages)
  const [selectedCategories, setSelectedCategories] = useState([]);

  // Control panel visibility & layout
  const [showControlPanel, setShowControlPanel] = useState(false);
  const [controlPanelLayout, setControlPanelLayout] = useState('modal');

  // Filter History tracking (max 50 entries)
  const [filterHistory, setFilterHistory] = useState([]);
  const historyRef = useRef([]);

  const recordHistory = useCallback((action, detail) => {
    const entry = { action, detail, timestamp: new Date().toISOString() };
    historyRef.current = [entry, ...historyRef.current].slice(0, 50);
    setFilterHistory([...historyRef.current]);
  }, []);

  // Recommended filter presets based on usage patterns
  const getRecommendations = useCallback(() => {
    const hist = historyRef.current;
    const recs = [];
    const topicCounts = {};
    hist.forEach(h => {
      if (h.action === 'toggle_ei_topic' && h.detail) {
        topicCounts[h.detail] = (topicCounts[h.detail] || 0) + 1;
      }
    });
    const sorted = Object.entries(topicCounts).sort((a, b) => b[1] - a[1]);
    if (sorted.length > 0) {
      recs.push({ type: 'frequent_topic', label: `You toggle "${sorted[0][0]}" most often`, topic: sorted[0][0] });
    }
    const certToggles = hist.filter(h => h.action === 'toggle_cert').length;
    if (certToggles > 5) {
      recs.push({ type: 'cert_heavy', label: 'You frequently toggle certification filters. Consider saving a preset.' });
    }
    return recs;
  }, []);

  // Increment counter to signal filter change to consumers
  const [filterVersion, setFilterVersion] = useState(0);
  const bumpVersion = useCallback(() => setFilterVersion(v => v + 1), []);

  // ============== UNDO / REDO ==============
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const MAX_UNDO = 30;

  const getSnapshot = useCallback(() => ({
    educatedIndividualFilters, eiMinWords, minWordCount,
    selectedDocTypes, docTypeMinWords,
    certificationFilters, certMinWords, contentMinWords,
    minQualityScore, exactMatch, searchDepth, aggregation,
    selectedCategories
  }), [educatedIndividualFilters, eiMinWords, minWordCount, selectedDocTypes, docTypeMinWords,
    certificationFilters, certMinWords, contentMinWords, minQualityScore, exactMatch, searchDepth,
    aggregation, selectedCategories]);

  const pushUndo = useCallback(() => {
    const snap = getSnapshot();
    undoStackRef.current = [snap, ...undoStackRef.current].slice(0, MAX_UNDO);
    redoStackRef.current = [];
    setCanUndo(true);
    setCanRedo(false);
  }, [getSnapshot]);

  const applySnapshot = useCallback((snap) => {
    if (!snap) return;
    if (snap.educatedIndividualFilters) setEducatedIndividualFilters(snap.educatedIndividualFilters);
    if (snap.eiMinWords) setEiMinWords(snap.eiMinWords);
    if (snap.minWordCount !== undefined) setMinWordCount(snap.minWordCount);
    if (snap.selectedDocTypes) setSelectedDocTypes(snap.selectedDocTypes);
    if (snap.docTypeMinWords) setDocTypeMinWords(snap.docTypeMinWords);
    if (snap.certificationFilters) setCertificationFilters(snap.certificationFilters);
    if (snap.certMinWords) setCertMinWords(snap.certMinWords);
    if (snap.contentMinWords) setContentMinWords(snap.contentMinWords);
    if (snap.minQualityScore !== undefined) setMinQualityScore(snap.minQualityScore);
    if (snap.exactMatch !== undefined) setExactMatch(snap.exactMatch);
    if (snap.searchDepth !== undefined) setSearchDepth(snap.searchDepth);
    if (snap.aggregation) setAggregation(snap.aggregation);
    if (snap.selectedCategories) setSelectedCategories(snap.selectedCategories);
    bumpVersion();
  }, [bumpVersion]);

  const undo = useCallback(() => {
    if (undoStackRef.current.length === 0) return;
    const current = getSnapshot();
    redoStackRef.current = [current, ...redoStackRef.current].slice(0, MAX_UNDO);
    const prev = undoStackRef.current.shift();
    applySnapshot(prev);
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(true);
    recordHistory('undo', '');
  }, [getSnapshot, applySnapshot, recordHistory]);

  const redo = useCallback(() => {
    if (redoStackRef.current.length === 0) return;
    const current = getSnapshot();
    undoStackRef.current = [current, ...undoStackRef.current].slice(0, MAX_UNDO);
    const next = redoStackRef.current.shift();
    applySnapshot(next);
    setCanRedo(redoStackRef.current.length > 0);
    setCanUndo(true);
    recordHistory('redo', '');
  }, [getSnapshot, applySnapshot, recordHistory]);

  // Wrap every setter to also bump version, record history, and push undo
  const wrap = useCallback((setter, actionName) => (valOrFn) => {
    pushUndo();
    setter(valOrFn);
    bumpVersion();
    if (actionName) recordHistory(actionName, typeof valOrFn === 'function' ? '' : JSON.stringify(valOrFn).slice(0, 80));
  }, [bumpVersion, recordHistory, pushUndo]);

  // Bulk load from saved preferences
  const loadPreferences = useCallback((prefs) => {
    if (!prefs) return;
    pushUndo();
    if (prefs.educatedIndividualFilters) setEducatedIndividualFilters(prefs.educatedIndividualFilters);
    if (prefs.eiMinWords) setEiMinWords(prefs.eiMinWords);
    if (prefs.minWordCount !== undefined) setMinWordCount(prefs.minWordCount);
    if (prefs.selectedDocTypes) setSelectedDocTypes(prefs.selectedDocTypes);
    if (prefs.docTypeMinWords) setDocTypeMinWords(prefs.docTypeMinWords);
    if (prefs.certificationFilters) setCertificationFilters(prefs.certificationFilters);
    if (prefs.certMinWords) setCertMinWords(prefs.certMinWords);
    if (prefs.contentMinWords) setContentMinWords(prefs.contentMinWords);
    if (prefs.minQualityScore !== undefined) setMinQualityScore(prefs.minQualityScore);
    if (prefs.exactMatch !== undefined) setExactMatch(prefs.exactMatch);
    if (prefs.searchDepth !== undefined) setSearchDepth(prefs.searchDepth);
    if (prefs.aggregation) setAggregation(prefs.aggregation);
    bumpVersion();
    recordHistory('load_preferences', 'Loaded saved preferences');
  }, [bumpVersion, recordHistory, pushUndo]);

  // Export current state as shareable object
  const exportFilters = useCallback(() => ({
    educatedIndividualFilters, eiMinWords, minWordCount,
    selectedDocTypes, docTypeMinWords,
    certificationFilters, certMinWords, contentMinWords,
    minQualityScore, exactMatch, searchDepth, aggregation
  }), [
    educatedIndividualFilters, eiMinWords, minWordCount,
    selectedDocTypes, docTypeMinWords,
    certificationFilters, certMinWords, contentMinWords,
    minQualityScore, exactMatch, searchDepth, aggregation
  ]);

  const value = useMemo(() => ({
    // EI (PRIMARY)
    educatedIndividualFilters, setEducatedIndividualFilters: wrap(setEducatedIndividualFilters, 'toggle_ei_topic'),
    eiMinWords, setEiMinWords: wrap(setEiMinWords, 'set_ei_min_words'),
    // Global word count
    minWordCount, setMinWordCount: wrap(setMinWordCount, 'set_min_word_count'),
    // Doc types
    documentTypes, selectedDocTypes, setSelectedDocTypes: wrap(setSelectedDocTypes, 'toggle_doc_type'),
    docTypeMinWords, setDocTypeMinWords: wrap(setDocTypeMinWords, 'set_doc_min_words'),
    // Cert (SECONDARY - unchecked by default)
    certificationFilters, setCertificationFilters: wrap(setCertificationFilters, 'toggle_cert'),
    certMinWords, setCertMinWords: wrap(setCertMinWords, 'set_cert_min_words'),
    // Content pref
    contentMinWords, setContentMinWords: wrap(setContentMinWords, 'set_content_min_words'),
    // Controls
    minQualityScore, setMinQualityScore: wrap(setMinQualityScore, 'set_quality'),
    exactMatch, setExactMatch: wrap(setExactMatch, 'toggle_exact_match'),
    searchDepth, setSearchDepth: wrap(setSearchDepth, 'set_depth'),
    aggregation, setAggregation: wrap(setAggregation, 'set_aggregation'),
    // Categories
    selectedCategories, setSelectedCategories: wrap(setSelectedCategories, 'select_categories'),
    // Control panel
    showControlPanel, setShowControlPanel,
    controlPanelLayout, setControlPanelLayout,
    // History & recommendations
    filterHistory, getRecommendations,
    // Persistence
    loadPreferences, exportFilters,
    // Version for sync detection
    filterVersion,
    // Undo/Redo
    undo, redo, canUndo, canRedo
  }), [
    educatedIndividualFilters, eiMinWords, minWordCount,
    documentTypes, selectedDocTypes, docTypeMinWords,
    certificationFilters, certMinWords, contentMinWords,
    minQualityScore, exactMatch, searchDepth, aggregation,
    selectedCategories, showControlPanel, controlPanelLayout,
    filterHistory, getRecommendations,
    loadPreferences, exportFilters,
    filterVersion, wrap,
    undo, redo, canUndo, canRedo
  ]);

  return (
    <FilterContext.Provider value={value}>
      {children}
    </FilterContext.Provider>
  );
};

export default FilterContext;
